/**
 * Stage map — the ONE place the UI knows the pipeline ladder.
 *
 * Mirrors `business-builder-backend/src/modules/prospecting/utils/stageGraph.ts`:
 *   APPLIED → PRE_SCREEN → INTERVIEW_SCHEDULED → INTERVIEW_DONE → REFERENCE_CHECK
 *           → DOCUMENT_CHECK → SUITABLE → PUT_FORWARD → PLACED
 *   exits: REJECTED | WITHDRAWN
 * Legacy values still present on old rows render as their canonical stage.
 * The server is the authority on whether a move is allowed; `allowedTargets`
 * only narrows the picker so the common case doesn't round-trip to a refusal.
 */
import { CandidateStage } from "../../../generated/graphql";

export const STAGE_LADDER: CandidateStage[] = [
  CandidateStage.Applied,
  CandidateStage.PreScreen,
  CandidateStage.InterviewScheduled,
  CandidateStage.InterviewDone,
  CandidateStage.ReferenceCheck,
  CandidateStage.DocumentCheck,
  CandidateStage.Suitable,
  CandidateStage.PutForward,
  CandidateStage.Placed,
];

export const EXIT_STAGES: CandidateStage[] = [CandidateStage.Rejected, CandidateStage.Withdrawn];

const LEGACY_ALIASES: Partial<Record<CandidateStage, CandidateStage>> = {
  [CandidateStage.CvReceived]: CandidateStage.Applied,
  [CandidateStage.ScreeningSent]: CandidateStage.Applied,
  [CandidateStage.ScreeningReplied]: CandidateStage.Applied,
  [CandidateStage.PhoneScreening]: CandidateStage.PreScreen,
  [CandidateStage.Shortlist]: CandidateStage.Suitable,
  [CandidateStage.OfferMade]: CandidateStage.Placed,
  [CandidateStage.Hired]: CandidateStage.Placed,
};

export const STAGE_LABELS: Record<CandidateStage, string> = {
  [CandidateStage.Applied]: "Applied",
  [CandidateStage.PreScreen]: "Pre-screen",
  [CandidateStage.InterviewScheduled]: "Interview scheduled",
  [CandidateStage.InterviewDone]: "Interview done",
  [CandidateStage.ReferenceCheck]: "Reference check",
  [CandidateStage.DocumentCheck]: "Document check",
  [CandidateStage.Suitable]: "Suitable",
  [CandidateStage.PutForward]: "Put forward",
  [CandidateStage.Placed]: "Placed",
  [CandidateStage.Rejected]: "Rejected",
  [CandidateStage.Withdrawn]: "Withdrawn",
  // legacy — never shown directly, kept so the record type is total
  [CandidateStage.CvReceived]: "Applied",
  [CandidateStage.ScreeningSent]: "Applied",
  [CandidateStage.ScreeningReplied]: "Applied",
  [CandidateStage.PhoneScreening]: "Pre-screen",
  [CandidateStage.Shortlist]: "Suitable",
  [CandidateStage.OfferMade]: "Placed",
  [CandidateStage.Hired]: "Placed",
};

/** Chakra colorScheme per canonical stage (tokens only — no hex). */
export const STAGE_COLORS: Record<CandidateStage, string> = {
  [CandidateStage.Applied]: "gray",
  [CandidateStage.PreScreen]: "blue",
  [CandidateStage.InterviewScheduled]: "cyan",
  [CandidateStage.InterviewDone]: "teal",
  [CandidateStage.ReferenceCheck]: "purple",
  [CandidateStage.DocumentCheck]: "orange",
  [CandidateStage.Suitable]: "green",
  [CandidateStage.PutForward]: "pink",
  [CandidateStage.Placed]: "green",
  [CandidateStage.Rejected]: "red",
  [CandidateStage.Withdrawn]: "gray",
  [CandidateStage.CvReceived]: "gray",
  [CandidateStage.ScreeningSent]: "gray",
  [CandidateStage.ScreeningReplied]: "gray",
  [CandidateStage.PhoneScreening]: "blue",
  [CandidateStage.Shortlist]: "green",
  [CandidateStage.OfferMade]: "green",
  [CandidateStage.Hired]: "green",
};

/** One-line explanation per stage for tooltips. */
export const STAGE_HELP: Record<CandidateStage, string> = {
  [CandidateStage.Applied]: "Application received — CV and details on file, nothing assessed yet.",
  [CandidateStage.PreScreen]: "Phone pre-screen in progress — the yes/no checklist and notes.",
  [CandidateStage.InterviewScheduled]: "Interview scheduling stage. Check the booking for the confirmed time and join link.",
  [CandidateStage.InterviewDone]: "Interview review stage. Check the record for feedback and outcome.",
  [CandidateStage.ReferenceCheck]: "Optional reference collection and follow-up.",
  [CandidateStage.DocumentCheck]: "Credentials being collected and assessed (police check, working rights, certificates).",
  [CandidateStage.Suitable]: "Manager considers the candidate suitable. Review outstanding evidence before putting forward.",
  [CandidateStage.PutForward]: "Recommended to the employer — waiting on their decision.",
  [CandidateStage.Placed]: "Hired by the employer — onboarding happens on their side.",
  [CandidateStage.Rejected]: "Exit — not proceeding. Reason on the record.",
  [CandidateStage.Withdrawn]: "Exit — the candidate withdrew.",
  [CandidateStage.CvReceived]: "Legacy value, shown as Applied.",
  [CandidateStage.ScreeningSent]: "Legacy value, shown as Applied.",
  [CandidateStage.ScreeningReplied]: "Legacy value, shown as Applied.",
  [CandidateStage.PhoneScreening]: "Legacy value, shown as Pre-screen.",
  [CandidateStage.Shortlist]: "Legacy value, shown as Suitable.",
  [CandidateStage.OfferMade]: "Legacy value, shown as Placed.",
  [CandidateStage.Hired]: "Legacy value, shown as Placed.",
};

export function canonicalStage(stage: CandidateStage | string | null | undefined): CandidateStage {
  if (!stage) return CandidateStage.Applied;
  const s = stage as CandidateStage;
  return LEGACY_ALIASES[s] || s;
}

export function isExitStage(stage: CandidateStage | string | null | undefined): boolean {
  return EXIT_STAGES.includes(canonicalStage(stage));
}

export function stageLabel(stage: CandidateStage | string | null | undefined): string {
  return STAGE_LABELS[canonicalStage(stage)] || String(stage || "");
}

export function stageColor(stage: CandidateStage | string | null | undefined): string {
  return STAGE_COLORS[canonicalStage(stage)] || "gray";
}

export function stageIndex(stage: CandidateStage | string | null | undefined): number {
  return STAGE_LADDER.indexOf(canonicalStage(stage));
}

/**
 * Targets the picker offers from `from`: one step forward, any step backward,
 * plus both exits. Exits themselves offer nothing — re-apply instead.
 */
export function allowedTargets(from: CandidateStage | string | null | undefined): CandidateStage[] {
  const c = canonicalStage(from);
  if (isExitStage(c)) return [];
  return [...STAGE_LADDER.filter((target) => target !== c && target !== CandidateStage.PutForward && target !== CandidateStage.Placed), ...EXIT_STAGES];
}

export const LADDER_TOOLTIP =
  "Pipeline ladder:\n" +
  STAGE_LADDER.map((s) => STAGE_LABELS[s]).join(" → ") +
  "\n\nExits: Rejected, Withdrawn. Recruitment steps can be skipped or revisited. Missing information stays visible. Put forward and placement use their dedicated actions.";
